import * as shape from 'd3-shape';
import { UserProfile } from 'fitbeat.gym-core';
import { TargetStatus, ProgramType, UserTargetTypes } from 'fitbeat.models';
import React, { useRef } from 'react';
import { translate } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import headerImage from '../../assets/images/progress-actor.png';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import Theme from '../../theme';
import { BodyMetricGraph } from '../../Tracking/components/BodyMetricGraph';
import { defaultScale, graphScaleLine, scaleImageAspectRatio } from '../../utils/scaling';
import { WeightTargetImageHeader } from './weight-target-image-header';
import { isMobileWeb } from '../../utils/ScreenSizes';

const WeightTargetSummaryComponent = ({
    t, targetStatus, goToTargetSetting,
    bodyWeightGraphData, goToPreviousStep, goToFaq, 
    onDone, isLoadingTarget, isResettingTarget, isProcessingWeightReview,
    userProfile, target
}) => {
    const hasMilestone = useRef(bodyWeightGraphData.data.some((x) => x.isMileStone));

    const strongAndFitWithScaleAndBodyWeightTarget =
      target.targetType === UserTargetTypes.Bodyweight &&
      userProfile.longTermGoal === ProgramType.GetStrongAndFitGoal &&
      UserProfile.hasSmartScale(userProfile);

    const canSetNewTarget =
      targetStatus === TargetStatus.TargetReached && !strongAndFitWithScaleAndBodyWeightTarget;

    return <View style={styles.flex}>
        <WeightTargetImageHeader
            goToPreviousStep={goToPreviousStep}
            imageStyle={isMobileWeb() ? styles.imageStyle : undefined}
            targetStatus={targetStatus}
            headerImage={headerImage} >
        <View style={styles.bgImageContent}>
            <View style={styles.flex}></View>
            <Text style={styles.resultText}>
                {
                    new Map([
                        [TargetStatus.TargetSet, t('weightTarget:allSet')],
                        [TargetStatus.MilestoneReached, t('weightTarget:wellDoneMilestone')],
                        [TargetStatus.TargetReached, t('weightTarget:wellDoneTarget')],
                        [TargetStatus.NotReached, t('weightTarget:tryHarder')],
                    ]).get(targetStatus)
                }
            </Text>
        </View>
        </WeightTargetImageHeader>
        <View style={styles.container}>
            <View style={styles.flex}>
                <View style={styles.graphLegendContainer}>
                    <View style={[styles.legendIcon, { backgroundColor: Theme.PRIMARY }]} />
                    <Text style={styles.legendText}>{t('workoutHistory:completed')}</Text>
                    {hasMilestone.current && <>
                        <View style={[styles.legendIcon, { backgroundColor: Theme.BLACK }]} />
                        <Text style={styles.legendText}>{t('workoutHistory:nextMilestone')}</Text>
                    </>}
                    {targetStatus !== TargetStatus.TargetReached && <>
                        <View style={styles.legendIcon} />
                        <Text style={styles.legendText}>{t('workoutHistory:target')}</Text>
                    </>}
                </View>
                <BodyMetricGraph
                    containerStyle={styles.graphContainer}
                    verticalGridLines={bodyWeightGraphData.verticalGridLines}
                    minDate={bodyWeightGraphData.minDate}
                    maxDate={bodyWeightGraphData.maxDate}
                    data={formatBodyMetricData(bodyWeightGraphData.data)}
                    clippingPoint={bodyWeightGraphData.clippingPoint}
                    curve={shape.curveMonotoneX}
                    unit={bodyWeightGraphData.unit}
                    showGraphText={true}
                />
            </View>

            <View style={styles.actionsContainer}>
            {canSetNewTarget && (
              <Button
                title={t('weightTarget:newTarget')}
                onPress={targetStatus === null ? goToFaq : goToTargetSetting}
                fontColor={Theme.PRIMARY}
                buttonType={ButtonTypes.TRANSPARENT}
                size={ButtonSize.NORMAL}
                containerStyle={{ marginBottom: defaultScale(20) }}
                isLoading={isLoadingTarget}
              />
            )}
            {targetStatus !== TargetStatus.TargetReached && (
              <Button
                title={t('weightTarget:changeTarget')}
                onPress={targetStatus === null ? goToFaq : goToTargetSetting}
                fontColor={Theme.PRIMARY}
                buttonType={ButtonTypes.TRANSPARENT}
                size={ButtonSize.NORMAL}
                containerStyle={{ marginBottom: defaultScale(20) }}
                isLoading={isLoadingTarget}
              />
            )}

            <Button
                disabled={isProcessingWeightReview || isResettingTarget}
                title={t('weightTarget:Done')}
                onPress={onDone}
                fontColor={Theme.WHITE}
                buttonType={ButtonTypes.NORMAL}
                size={ButtonSize.NORMAL}
            />
            </View>
        </View>
    </View>;
};

const formatBodyMetricData = (bodyMetric) => {
    return bodyMetric.map(x => {
        return {...x, value: Math.round(x.value * 10) / 10}
    });
}

const styles = StyleSheet.create({
    container: {
        marginHorizontal: defaultScale(15),
        flex: 1,
    },
    flex: {
        flexGrow: 1
    },
    actionsContainer: {
        marginTop: defaultScale(40),
        paddingBottom: defaultScale(40),
    },
    graphContainer: {
        height: Platform.OS === 'web' ? defaultScale(180) : undefined,
        width: '100%',
        aspectRatio: 2.2,
    },
    graphLegendContainer: {
        flexDirection: 'row',
        marginBottom: defaultScale(25),
        marginTop: defaultScale(35),
        paddingLeft: defaultScale(3),
    },
    legendText: {
        ...Theme.graphStyle.regular12,
        color: Theme.BLACK,
        marginRight: defaultScale(30),
    },
    legendIcon: {
        width: graphScaleLine(11),
        height: graphScaleLine(11),
        borderRadius: graphScaleLine(6),
        marginRight: defaultScale(4),
        alignSelf: 'center',
        backgroundColor: '#CCCCCC',
    },
    bgImageContent: {
        flex: 1,
        justifyContent: 'flex-end',
    },
    resultText: {
        ...Theme.textStyle.heading,
        color: Theme.GREY_LIGHT,
        marginHorizontal: defaultScale(22),
        paddingBottom: defaultScale(25),
        marginTop: defaultScale(15),
    },
    imageStyle: {
        width: '100%',
        ...Platform.select({
          web: {
            height: '40vh',
          },
          default: {
            aspectRatio: scaleImageAspectRatio(1),
          },
        }),
    },
});

export const WeightTargetSummary = translate(['weightTarget, workoutHistory'])(WeightTargetSummaryComponent);
